import type { FieldValidator, Function } from '@lucidtech/las-sdk-browser';
import { useCallback, useEffect, useState } from 'react';

import { useBatchGetFile, useBatchGetFunctions } from '@/hooks/api';
import { selectFromFieldConfig, useDocumentStore } from '@/store';
import { FieldContextType, FieldStateValueType, importJS, ValidatorFnType } from '@/utils';

type ValidatorResolved = FieldValidator & {
  function?: Function;
  transform?: ValidatorFnType;
};

export type UseValidatorsOpts = {
  name: string;
};

export enum ValidationType {
  Error,
  Warning,
}

export class ValidationError extends Error {
  type: ValidationType;
  message: string;

  constructor(type: ValidationType, message: string) {
    super(message);
    this.type = type;
    this.message = message;
  }
}

export type ValidatorsFnReturnType = {
  result?: FieldStateValueType;
  meta: { id: string; warnings: string[]; errors: string[] }[];
};

export const useValidators = ({ name }: UseValidatorsOpts) => {
  const config = useDocumentStore(selectFromFieldConfig(name));
  const { data: functions } = useBatchGetFunctions(config?.validators?.map(({ functionId }) => ({ functionId })));
  const { data: validatorBlobs } = useBatchGetFile(functions?.map((f) => ({ fileUrl: f.fileUrl })));

  const [validators, setValidators] = useState<ValidatorResolved[]>([]);

  useEffect(() => {
    const loadValidators = async () => {
      const validators: ValidatorResolved[] = config?.validators?.map((f) => ({ ...f })) ?? [];
      if (validators.length === functions.length) {
        for (const [i, validator] of validators.entries()) {
          validator.function = functions[i];
          const validatorBlob = validatorBlobs[i];
          if (!validatorBlob) continue;
          validator.transform = (await importJS(validatorBlob)).default;
        }
      }

      setValidators(validators);
    };

    loadValidators();
  }, [config?.validators, functions, name, validatorBlobs]);

  const run = useCallback(
    async (state: FieldStateValueType | undefined, context: FieldContextType): Promise<ValidatorsFnReturnType> => {
      let newState = state;
      const meta = [];

      if (validators) {
        for (const validator of validators) {
          if (!validator) continue;

          const validatorContext = structuredClone(context);
          validatorContext.config = structuredClone(validator.config);
          if (!validator.transform) continue;

          const id = validator.id;

          try {
            const {
              result,
              errors: newErrors,
              warnings: newWarnings,
            } = validator.transform({ event: structuredClone(newState ?? {}), context: validatorContext });

            newState = result;
            meta.push({ id, warnings: newWarnings ?? [], errors: newErrors ?? [] });

            if ((newErrors ?? []).length > 0) return { meta };
          } catch (error) {
            if (error instanceof Error) {
              if (context.config.validationMode === 'error') {
                meta.push({ id, warnings: [], errors: [error.message] });
              } else {
                meta.push({ id, warnings: [error.message], errors: [] });
              }
            }

            return { meta };
          }
        }
      }

      return { result: newState, meta };
    },
    [validators]
  );

  return { run };
};
